import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type DeveloperMemberRole =
  | 'developer_admin'
  | 'developer_sales_agent'
  | 'developer_sales_lead'
  | 'developer_project_manager'
  | 'developer_finance';

export interface DeveloperContext {
  developerId: string | null;
  developerName: string | null;
  isAdmin: boolean;
  memberRole: DeveloperMemberRole | null;
  canManageTeam: boolean;
  canManageRules: boolean;
  canManageBookings: boolean;
}

const EMPTY: DeveloperContext = {
  developerId: null,
  developerName: null,
  isAdmin: false,
  memberRole: null,
  canManageTeam: false,
  canManageRules: false,
  canManageBookings: false,
};

export function useDeveloperContext(): { ctx: DeveloperContext; isLoading: boolean } {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['developer-context', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<DeveloperContext> => {
      // Are we the admin (owner of a developer_profile)?
      const { data: dp } = await supabase
        .from('developer_profiles')
        .select('id, company_name')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (dp?.id) {
        return {
          developerId: dp.id,
          developerName: dp.company_name,
          isAdmin: true,
          memberRole: 'developer_admin',
          canManageTeam: true,
          canManageRules: true,
          canManageBookings: true,
        };
      }
      // Otherwise, are we a team member?
      const { data: tm } = await supabase
        .from('developer_team_members')
        .select('developer_id, member_role, developer_profiles(company_name)')
        .eq('user_id', user!.id)
        .eq('is_active', true)
        .order('joined_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (tm?.developer_id) {
        const role = tm.member_role as DeveloperMemberRole;
        return {
          developerId: tm.developer_id,
          developerName: (tm as any).developer_profiles?.company_name ?? null,
          isAdmin: false,
          memberRole: role,
          canManageTeam: false,
          canManageRules: false,
          canManageBookings: role === 'developer_project_manager' || role === 'developer_finance',
        };
      }
      return EMPTY;
    },
  });

  return { ctx: data ?? EMPTY, isLoading };
}
